"use client";

import { useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useStoreContext } from "@components/store/provider";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import ShadowCard from "@sharedComponents/cards/shadow-card/";
import RenderProductCarousel from "@components/widgets/shared/product-carousel";

import useFetchMenuIdBasedProductList from "@hooks/search/use-new-fetch-menu-based-product-list";
import useDraftCatalogSelectedProductsPresence from "@hooks/automated-catalog/use-draft-catalog-selected-products-presence";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useWeglot } from "@stadium/services/weglot/provider";

import { getSelectedLanguage, isForceTranslationsEnabled } from "@stadium/services/weglot/helpers";
import { generateUrlByQuery } from "@stadium/helpers/dom";
import { generateQueryForRoutePath } from "@stadium/helpers/store";

import {
  getIsHideBulkPricing,
  getStoreNumber,
  getStoreWithWorkspaceSlug,
  getStoreSlug,
} from "@selectors/store";

import Product from "@sharedComponents/cards/product";

import { PRODUCT_VIEW_PATHNAME } from "@constants/routes";
import { OLD_PRODUCT_VIEW_PATHNAME } from "@constants/old-routes";
import { DEFAULT_LANG_OBJ } from "@stadium/services/weglot/constants";
import styles from "./main.module.scss";

const emptyObj = {};

const INITIAL_PAGE = 1;

const FeaturedProducts = ({
  products,
  variables,
  count,
  showPagination = false,
  productListFetchConfig = emptyObj,
  query,
  productProps = emptyObj,
  colCardClass = "",
  containerClass = "",
  isCarousel = false,
  isSwagCreationShopFlow = false,
  eventLocationAnalytics,
}) => {
  const skipFirstFetchRef = useRef(true);

  const searchParams = useSearchParams();
  const forceTranslations = isForceTranslationsEnabled(searchParams);

  const { countryCode, isNewRoute, lang, workspaceSlug } = useStoreContext();
  const { preferredColorNames, currentUserConfig } = useCreateSwagContext();

  const { isDesigner = false } = currentUserConfig || {};

  const storeNumber = useSelector(getStoreNumber);
  const slug = useSelector(getStoreSlug);
  const storeWithWorkspaceSlug = useSelector(getStoreWithWorkspaceSlug);

  const { availableLanguages, instance } = useWeglot();

  const { isSendToMyself, isSendToOthers } = useSendItemsContext();

  const selectedLanguage = getSelectedLanguage({
    weglotLanguages: availableLanguages,
    langInUrl: forceTranslations ? lang : "",
    weglotInstance: instance,
    forceTranslations,
  });

  const { getStoreUpdatedAt } = usePreviewModeContext();

  const hideBulkPricing = useSelector(getIsHideBulkPricing);

  const storeVariables = useMemo(
    () => ({
      ...variables,
      storeUpdatedAt: getStoreUpdatedAt(),
      ...(isSwagCreationShopFlow && {
        color: preferredColorNames?.map(({ presentation }) => presentation) || [],
        designer: isDesigner,
      }),
    }),
    [getStoreUpdatedAt, preferredColorNames, isSwagCreationShopFlow, isDesigner],
  );

  const { loading, fetchedProducts, loadMoreRef, resetProductList } =
    useFetchMenuIdBasedProductList({
      variables: storeVariables,
      initialProducts: products,
      shouldSkip: (page) => {
        if (isSwagCreationShopFlow) {
          return false;
        }
        if (!hideBulkPricing) {
          return page === INITIAL_PAGE;
        }

        if (skipFirstFetchRef.current) {
          return isSendToMyself && page === INITIAL_PAGE;
        }

        return false;
      },
      shouldReplaceList: isSwagCreationShopFlow,
      ...productListFetchConfig,
    });
  const { fetchSelectedProductsPresence } = useDraftCatalogSelectedProductsPresence();

  useEffect(() => {
    if (skipFirstFetchRef.current && isSendToOthers) {
      skipFirstFetchRef.current = false;
    }
    if (!skipFirstFetchRef.current && hideBulkPricing) {
      resetProductList();
      window.scrollTo(0, 0);
    }
  }, [isSendToOthers]);

  useEffect(() => {
    if (isSwagCreationShopFlow && fetchedProducts.length) {
      const productIds = fetchedProducts.map(({ productId }) => Number(productId)).filter(Boolean);
      fetchSelectedProductsPresence(productIds);
    }
  }, [fetchedProducts, fetchSelectedProductsPresence, isSwagCreationShopFlow]);

  const totalFetchedProducts = fetchedProducts?.length;

  const renderShimmerLoader = () => {
    return (
      <ShimmerWrapper
        length={count}
        cardClass={styles.colCard}
        wrapperClass={styles.productCardsWrapper}
      >
        <ShadowCard />
      </ShimmerWrapper>
    );
  };

  const renderProducts = () =>
    fetchedProducts.map((product) => {
      const productLink = generateUrlByQuery({
        pathname: isNewRoute ? PRODUCT_VIEW_PATHNAME : OLD_PRODUCT_VIEW_PATHNAME,
        query: generateQueryForRoutePath({
          lang: selectedLanguage ?? DEFAULT_LANG_OBJ.key,
          productSlug: product?.slug,
          countryCode: isSwagCreationShopFlow ? "" : countryCode,
          storeNumber,
          slug: isNewRoute ? slug : storeWithWorkspaceSlug,
          isNewRoute,
          workspaceSlug,
          ...query,
        }),
      });

      return (
        <Product
          data={product}
          className={`${styles.colCard} ${colCardClass}`}
          key={product.id}
          source="search"
          isNextImage={false}
          unoptimized
          productLink={productLink}
          {...productProps}
          eventLocationAnalytics={
            eventLocationAnalytics?.track
              ? {
                  ...eventLocationAnalytics,
                  eventProperties: {
                    ...eventLocationAnalytics.eventProperties,
                    product_id: product.id,
                    product_name: product.name,
                  },
                }
              : undefined
          }
        />
      );
    });

  return isCarousel || isSwagCreationShopFlow ? (
    <RenderProductCarousel
      isSwagCreationShopFlow={isSwagCreationShopFlow}
      totalFetchedProducts={totalFetchedProducts}
      showDots={false}
    >
      {renderProducts()}
    </RenderProductCarousel>
  ) : (
    <>
      <article className={containerClass}>{renderProducts()}</article>
      {showPagination && <div ref={loadMoreRef}>{loading && renderShimmerLoader()}</div>}
    </>
  );
};

export default FeaturedProducts;
